/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-return */
import type {
  BaseQueryFn,
  MutationActionCreatorResult,
  QueryActionCreatorResult,
  TypedLazyQueryTrigger,
  TypedMutationTrigger,
} from '@reduxjs/toolkit/query/react';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
  decrementLoader,
  incrementLoader,
  setError,
} from '../store/slices/ui-slice';
import type { EnhancedApiOptions } from '../types/api-types';

export type EnhancedPromise<T> = T & {
  withOptions: (options: EnhancedApiOptions) => T;
};

export type EnhancedLazyTrigger<
  ResultType,
  QueryArg,
  BaseQuery extends BaseQueryFn,
> = (
  ...args: Parameters<TypedLazyQueryTrigger<ResultType, QueryArg, BaseQuery>>
) => EnhancedPromise<
  ReturnType<TypedLazyQueryTrigger<ResultType, QueryArg, BaseQuery>>
>;

export type EnhancedMutationTrigger<
  ResultType,
  QueryArg,
  BaseQuery extends BaseQueryFn,
> = (
  ...args: Parameters<TypedMutationTrigger<ResultType, QueryArg, BaseQuery>>
) => EnhancedPromise<
  ReturnType<TypedMutationTrigger<ResultType, QueryArg, BaseQuery>>
>;

type ActionCreatorResult =
  | QueryActionCreatorResult<any>
  | MutationActionCreatorResult<any>;

const useEnhancedApi = <
  Trigger extends (...args: any[]) => ActionCreatorResult,
  Rest extends any[],
>(
  useHook: (...args: any[]) => [Trigger, ...Rest],
  ...hookArgs: any[]
): [Trigger, ...Rest] => {
  const dispatch = useDispatch();
  const [trigger, ...rest] = useHook(...hookArgs);

  const wrappedTrigger = useMemo(() => {
    return (...args: Parameters<Trigger>) => {
      const originalPromise = trigger(...args);

      const extensions = {
        withOptions: (
          { showLoader, showError }: EnhancedApiOptions = {
            showLoader: false,
            showError: false,
          }
        ) => {
          if (showLoader) {
            dispatch(incrementLoader());
            void originalPromise.finally(() => {
              dispatch(decrementLoader());
            });
          }

          if (showError) {
            void originalPromise.then(result => {
              if (result.error) {
                dispatch(setError(result.error as Error));
              }
            });
          }

          return proxy;
        },
      };

      const proxy = new Proxy(originalPromise, {
        get(target, prop, receiver) {
          if (prop === 'withOptions') {
            return extensions.withOptions;
          }
          const value = Reflect.get(target, prop, receiver);
          return typeof value === 'function' ? value.bind(target) : value;
        },
      });

      return proxy;
    };
  }, [trigger, dispatch]);

  return [wrappedTrigger, ...rest] as unknown as [Trigger, ...Rest];
};

export default useEnhancedApi;
